
import { ApiResponse } from '@shared/types/api';
import { User } from '@shared/types/user';
import { getAuth } from 'firebase/auth';
import { getDownloadURL, getStorage, ref, uploadBytes } from 'firebase/storage';
import api from '../../../shared/services/api';
import { UpdatePasswordDto, UpdateProfileDto } from '../types/settings.types';

export class SettingsService {
  private static readonly BASE_URL = '/users';

  /**
   * Get current user profile
   */
  static async getProfile(options?: { include?: string[] }): Promise<User> {
    const response = await api.get<ApiResponse<User>>(`${this.BASE_URL}/profile`, { params: { include: options?.include } });
    return response.data.data;
  }

  /**
   * Get user profile by ID
   */
  static async getUserProfile(userId: string, options?: { include?: string[] }): Promise<User> {
    const response = await api.get<ApiResponse<User>>(`${this.BASE_URL}/profile/${userId}`, {
      params: { include: options?.include }
    });
    return response.data.data;
  }

  /**
   * Update user profile
   */
  static async updateProfile(data: UpdateProfileDto): Promise<User> {
    const response = await api.put<ApiResponse<User>>(`${this.BASE_URL}/profile`, data);
    return response.data.data;
  }

  /**
   * Update password
   */
  static async updatePassword(data: UpdatePasswordDto): Promise<void> {
    await api.put<ApiResponse<void>>(`${this.BASE_URL}/password`, {
      currentPassword: data.currentPassword,
      newPassword: data.newPassword,
    });
  }

  /**
   * Upload profile image
   */
  static async uploadProfileImage(file: File): Promise<User> {
    try {
      // Generate unique filename
      const timestamp = Date.now();
      const imageId = crypto.randomUUID();
      const userId = await this.getCurrentUserId();
      const originalFileName = `user-profiles/${userId}/${timestamp}_${imageId}`;

      // Get storage reference
      const storage = getStorage();
      const storageRef = ref(storage, originalFileName);

      // Upload file with metadata
      const metadata = {
        contentType: file.type,
        customMetadata: {
          userId,
          uploadedFrom: window.location.origin
        }
      };

      // Upload file
      console.log('Uploading file to:', originalFileName);
      const uploadResult = await uploadBytes(storageRef, file, metadata);
      console.log('Upload completed:', uploadResult);

      const imageUrl = await getDownloadURL(storageRef);
      console.log('Download URL obtained:', imageUrl);

      // Send URL to backend for processing
      const response = await api.post<ApiResponse<User>>(
        `${this.BASE_URL}/profile/image/process`,
        { imageUrl }
      );

      return response.data.data;
    } catch (error) {
      console.error('Error uploading image:', error);
      if (error instanceof Error) {
        throw new Error(`Error uploading image: ${error.message}`);
      }
      throw error;
    }
  }

  private static async getCurrentUserId(): Promise<string> {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');
    return user.uid;
  }
}
